import { FormHelperText, Typography } from '@mui/material'
import { Product } from '../../SwapOrReturnTypes'
import { useCallback, useMemo, useState } from 'react'
import { limitCharacters } from 'src/utils/formatString'
import { InfoReturnOptions } from 'src/features/receivement/ReceivementTypes'
import { useDispatch } from 'react-redux'
import { actions } from '../../SwapOrReturnState'
import { PostAddRounded, CheckRounded, CloseRounded } from '@mui/icons-material'
import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetError,
} from 'react-hook-form'
import TableMui from 'src/components/TableMui'
import BatchSelectionModal from '../BatchSelectionModal/BatchSelectionModal'
import * as S from './SelectedProductsTable.styles'

interface SelectedProductsTableProps {
  selectedProducts: Product[]
  infoReturnOptions: InfoReturnOptions | null
  searchTerm: string
  control: Control<any>
  errors: FieldErrors<any>
  readOnly: boolean
  setError: UseFormSetError<any>
  clearErrors: UseFormClearErrors<any>
}

const SelectedProductsTable: React.FC<SelectedProductsTableProps> = ({
  selectedProducts,
  infoReturnOptions,
  searchTerm,
  control,
  errors,
  readOnly,
  setError,
  clearErrors,
}) => {
  const [openBatchModal, setOpenBatchModal] = useState<boolean>(false)
  const [currentProductToAddBatches, setCurrentProductToAddBatches] = useState<Product>()

  const showBatchModal = useCallback((product: Product) => {
    setCurrentProductToAddBatches(product)
    setOpenBatchModal(true)
  }, [])

  const closeBatchModal = () => {
    setCurrentProductToAddBatches(undefined)
    setOpenBatchModal(false)
  }

  const filteredSelectedProducts = selectedProducts.filter((product) =>
    product.productCode.includes(searchTerm) || product.description.toLowerCase().includes(searchTerm.toLowerCase()))

  const dispatch = useDispatch()

  const onDeselect = useCallback((product: Product, index: number) => {
    dispatch(actions.setDeselectProduct(product))
    clearErrors(`products.${index}.quantity`)
  }, [clearErrors, dispatch])

  const validateFieldValues = useCallback((product: Product, inputQuantity: string, index: number) => {
    if (inputQuantity === '') {
      setError(`products.${index}.quantity`, {
        message: 'Campo obrigatório',
      })
      return
    }

    const convertedInputQuantity = Number(inputQuantity)

    if (convertedInputQuantity <= 0) {
      setError(`products.${index}.quantity`, {
        message: 'Deve ser maior que zero',
      })
      return
    }

    if (convertedInputQuantity > product.availablePendingQuantity) {
      setError(`products.${index}.quantity`, {
        message: 'Qtd. maior que o disponível',
      })
      return
    }

    if (!infoReturnOptions?.allowBreakUnitOnInvoiceReturn && (convertedInputQuantity % product.quantityPerBox !== 0)) {
      setError(`products.${index}.quantity`, {
        message: 'Deve respeitar caixaria',
      })
      return
    }

    clearErrors(`products.${index}.quantity`)
  }, [clearErrors, setError, infoReturnOptions?.allowBreakUnitOnInvoiceReturn])

  const updateInputQuantity = useCallback((product: Product, inputQuantity: string, index: number) => {
    validateFieldValues(product, inputQuantity, index)

    const convertedInputQuantity = Number(inputQuantity)

    dispatch(actions.setInputQuantity({ product, inputQuantity: convertedInputQuantity }))
  }, [dispatch, validateFieldValues])

  const resolveTableRows = useMemo(() => filteredSelectedProducts.map((product, index) => ({
    ...product,
    description: (
      <S.CustomTooltip arrow title={product.description}>
        <Typography fontSize={14}>
          {limitCharacters(product.description, 22)}
        </Typography>
      </S.CustomTooltip>
    ),
    measureUnit: product.measureUnit.toLowerCase(),
    totalInvoiceQuantity: product.totalInvoiceQuantity.toString(),
    availablePendingQuantity: product.availablePendingQuantity.toString(),
    inputQuantity: (
      <Controller
        name={`products[${index}].quantity`}
        control={control}
        render={({ field }) => (
          <>
            <S.CustomTextField
              {...field}
              id='input-quantity'
              type='number'
              autoComplete='off'
              label='Qtd. pend.'
              size='small'
              disabled={readOnly}
              fullWidth
              value={product.inputQuantity || ''}
              onChange={(e) => {
                const { value } = e.target
                field.onChange(e)
                updateInputQuantity(product, value, index)
              }}
              error={errors.products && errors.products[index]}
              inputProps={{
                step: !infoReturnOptions?.allowBreakUnitOnInvoiceReturn ? product.quantityPerBox : 1,
                min: !infoReturnOptions?.allowBreakUnitOnInvoiceReturn ? product.quantityPerBox : 1,
                max: product.availablePendingQuantity,
                'data-testid': 'input-quantity-test-id',
              }}
            />
            {errors.products && errors.products[index] && errors.products[index].quantity ? (
              <FormHelperText error>
                {errors.products[index].quantity.message}
              </FormHelperText>
            ) : ''}
          </>
        )}
      />
    ),
    action: (
      <S.CustomButton
        title='Excluir'
        bgColor='cancel'
        hoverColor='cancelHover'
        letterColor='white'
        data-testid='remove-product-button-test-id'
        disabled={readOnly}
        onClick={() => onDeselect(product, index)}
      />
    ),
    addBatches: (
      <S.AddBatchesContainer>
        <S.CustomButton
          startIcon={<PostAddRounded />}
          title='Lotes'
          bgColor='lightBlue5'
          hoverColor='darkBlueHover2'
          letterColor='white'
          data-testid='select-batches-button-test-id'
          disabled={!product.inputQuantity || (errors.products && errors.products[index])}
          onClick={() => showBatchModal(product)}
        />
        { product.isQuantityMatch ? <CheckRounded color='success'/> : <CloseRounded color='error'/> }
        {
          currentProductToAddBatches && (
            <BatchSelectionModal
              show={openBatchModal}
              idModel={currentProductToAddBatches.idModel}
              batches={currentProductToAddBatches.batches}
              totalPending={currentProductToAddBatches.inputQuantity}
              readOnly={readOnly}
              closeBatchModal={closeBatchModal}
            />
          )
        }
      </S.AddBatchesContainer>
    ),
  })), [
    control,
    currentProductToAddBatches,
    errors.products,
    filteredSelectedProducts,
    infoReturnOptions?.allowBreakUnitOnInvoiceReturn,
    onDeselect,
    openBatchModal,
    readOnly,
    showBatchModal,
    updateInputQuantity,
  ])

  return (
    <S.Container>
      <Typography variant='h5' fontWeight='bold'>Produtos selecionados</Typography>
      <TableMui
        columns={[
          { id: 'productCode', label: 'Cód. produto' },
          { id: 'description', label: 'Desc. produto' },
          { id: 'measureUnit', label: 'Un. medida' },
          { id: 'quantityPerBox', label: 'Caixaria' },
          { id: 'totalInvoiceQuantity', label: 'Qtd. total NF' },
          { id: 'availablePendingQuantity', label: 'Qtd. disponível' },
          { id: 'inputQuantity', label: 'Qtd. pendência' },
          { id: 'addBatches', label: 'Adicionar lotes' },
          { id: 'action', label: 'Ação' },
        ]}
        emptyLabel={selectedProducts.length > 0 && filteredSelectedProducts.length === 0 ? 'Nenhum produto encontrado' : 'Nenhum produto selecionado'}
        rows={resolveTableRows}
        enablePagination
        rowsPerPageLimit={10}
        maxHeight='60vh'
        minHeight='60vh'
        testId='selected-products-table-test-id'
      />
    </S.Container>
  )
}

export default SelectedProductsTable
